import React, { useEffect, useRef, useState } from "react";
import { Loader2, Mic, Pause, Play, Send, Trash2 } from "lucide-react";

const formatTime = (seconds) => {
  const value = Math.max(0, Math.floor(seconds || 0));
  return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, "0")}`;
};

const WAVEFORM_BARS = 64;
const summarizeLevels = (samples) => Array.from({ length: WAVEFORM_BARS }, (_, index) => {
  const start = Math.floor(index * samples.length / WAVEFORM_BARS);
  const end = Math.max(start + 1, Math.floor((index + 1) * samples.length / WAVEFORM_BARS));
  let peak = 0;
  for (let i = start; i < end; i++) peak = Math.max(peak, samples[i] || 0);
  return peak;
});

export default function VoiceRecordingControls({
  isRecording, isPaused, isPreparing, isSending, audioUrl, duration,
  stream, audioRef, onPause, onResume, onDiscard, onSend, onPlaybackError,
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [mediaDuration, setMediaDuration] = useState(0);
  const [levels, setLevels] = useState(() => Array(WAVEFORM_BARS).fill(0));
  const samplesRef = useRef([]);
  const pausedRef = useRef(isPaused);
  pausedRef.current = isPaused;
  const busy = isPreparing || isSending;
  const reviewing = isPaused || !isRecording;
  const total = Math.max(mediaDuration, duration, 0.1);
  const progress = reviewing ? Math.min(position / total, 1) : 0;

  useEffect(() => {
    const player = audioRef.current;
    setPosition(0);
    setMediaDuration(0);
    setIsPlaying(false);
    return () => player?.pause();
  }, [audioUrl, audioRef]);

  useEffect(() => {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!stream || !AudioContextClass) return;

    let context, source, frame;
    samplesRef.current = [];
    try {
      context = new AudioContextClass();
      source = context.createMediaStreamSource(stream);
      const analyser = context.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const data = new Uint8Array(analyser.fftSize);
      let lastSample = 0;
      const sample = (now) => {
        if (!pausedRef.current && now - lastSample >= 100) {
          analyser.getByteTimeDomainData(data);
          let energy = 0;
          for (const value of data) energy += ((value - 128) / 128) ** 2;
          const level = Math.min(1, Math.sqrt(energy / data.length) * 4);
          samplesRef.current.push(level);
          const recent = samplesRef.current.slice(-WAVEFORM_BARS);
          setLevels(Array(WAVEFORM_BARS - recent.length).fill(0).concat(recent));
          lastSample = now;
        }
        frame = requestAnimationFrame(sample);
      };
      frame = requestAnimationFrame(sample);
      context.resume().catch(() => {});
    } catch {
      // Waveform visualization is optional; it must never interrupt recording.
    }
    return () => {
      cancelAnimationFrame(frame);
      source?.disconnect();
      context?.close().catch(() => {});
    };
  }, [stream]);

  useEffect(() => {
    if (isPaused && samplesRef.current.length) {
      setLevels(summarizeLevels(samplesRef.current));
    }
  }, [isPaused]);

  const updateMediaDuration = (event) => {
    const player = event.currentTarget;
    const value = Number.isFinite(player.duration) && player.duration > 0
      ? player.duration
      : player.buffered.length ? player.buffered.end(player.buffered.length - 1) : 0;
    if (value > 0) setMediaDuration(value);
  };

  const togglePlayback = async () => {
    const player = audioRef.current;
    if (!player || busy) return;
    if (!player.paused) {
      player.pause();
      return;
    }
    try {
      if (player.ended) player.currentTime = 0;
      await player.play();
    } catch {
      onPlaybackError();
    }
  };

  return (
    <div aria-busy={busy} className="flex-1 min-w-0 max-w-xl mx-auto flex items-center gap-2 sm:gap-3 bg-white border border-gray-200 rounded-full px-2 sm:px-4 py-2 shadow-sm">
      <button
        type="button" onClick={onDiscard} disabled={busy}
        title="Descartar áudio" aria-label="Descartar áudio"
        className="shrink-0 p-2 text-gray-500 hover:text-red-500 disabled:opacity-50 rounded-full transition-colors"
      >
        <Trash2 className="w-5 h-5" />
      </button>

      {reviewing ? (
        <button
          type="button" onClick={togglePlayback} disabled={!audioUrl || busy}
          title={isPlaying ? "Pausar reprodução" : "Reproduzir áudio"}
          aria-label={isPlaying ? "Pausar reprodução" : "Reproduzir áudio"}
          className="shrink-0 p-2 text-gray-600 hover:text-green-600 disabled:opacity-50 rounded-full"
        >
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 fill-current" />}
        </button>
      ) : (
        <span className="shrink-0 w-2.5 h-2.5 mx-1 rounded-full bg-rose-400 animate-pulse" aria-label="Gravando áudio" />
      )}

      <div className="relative flex-1 min-w-[40px] h-8 flex items-center gap-px sm:gap-0.5 overflow-hidden rounded focus-within:ring-2 focus-within:ring-green-500">
        {levels.map((level, index) => (
          <span
            key={index} aria-hidden="true"
            className={`flex-1 min-w-0 rounded-full ${index % 2 ? "hidden sm:block" : ""} ${reviewing && index / levels.length < progress ? "bg-green-500" : "bg-gray-400"}`}
            style={{ height: `${Math.max(3, level * 28)}px` }}
          />
        ))}
        {reviewing && audioUrl && (
          <input
            type="range" min="0" max={total} step="0.01"
            value={Math.min(position, total)} disabled={busy}
            aria-label="Posição do áudio" aria-valuetext={`${formatTime(position)} de ${formatTime(total)}`}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            onChange={(event) => {
              const value = Number(event.target.value);
              if (audioRef.current) {
                audioRef.current.currentTime = value;
                setPosition(value);
              }
            }}
          />
        )}
      </div>

      <span className="shrink-0 min-w-[36px] text-sm sm:text-base font-medium tabular-nums text-gray-700">
        {formatTime(reviewing ? (isPlaying || position > 0 ? position : total) : duration)}
      </span>

      {isRecording && (
        <button
          type="button" onClick={isPaused ? onResume : onPause} disabled={busy}
          title={isPaused ? "Continuar gravação" : "Pausar gravação"}
          aria-label={isPaused ? "Continuar gravação" : "Pausar gravação"}
          className="shrink-0 p-2 text-rose-500 hover:bg-rose-50 disabled:opacity-50 rounded-full transition-colors"
        >
          {isPaused ? <Mic className="w-5 h-5" /> : <Pause className="w-5 h-5 fill-current" />}
        </button>
      )}

      <button
        type="button" onClick={onSend} disabled={busy}
        title="Enviar áudio" aria-label="Enviar áudio"
        className="shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-green-500 hover:bg-green-600 text-white disabled:opacity-50 shadow-sm transition-colors"
      >
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5 ml-0.5" />}
      </button>

      {audioUrl && (
        <audio
          key={audioUrl} ref={audioRef} src={audioUrl} preload="metadata"
          aria-label="Prévia do áudio gravado" className="hidden"
          onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)}
          onEnded={() => { setIsPlaying(false); setPosition(0); }}
          onTimeUpdate={(event) => setPosition(event.currentTarget.currentTime)}
          onLoadedMetadata={updateMediaDuration} onLoadedData={updateMediaDuration}
          onProgress={updateMediaDuration} onDurationChange={updateMediaDuration}
          onError={onPlaybackError}
        />
      )}
    </div>
  );
}
